/**
 * Reading plan definitions. Each plan is just a book filter + a day count —
 * the actual day-by-day chapter schedule is computed on the fly from the
 * book index already loaded by the app, so no external data or hand-authored
 * day lists are needed.
 */
export const READING_PLANS = [
	{
		id: 'bible-year',
		name: 'Bible in a Year',
		description: 'The whole Bible across 365 days, with an Old and New Testament reading paired together every day.',
		days: 365,
		filter: () => true,
		mixed: true,
	},
	{
		id: 'nt-30',
		name: 'New Testament in 30 Days',
		description: 'All 27 New Testament books in one month.',
		days: 30,
		filter: (book) => book.testament === 'NT',
	},
	{
		id: 'gospels-14',
		name: 'The Gospels in 2 Weeks',
		description: 'Matthew, Mark, Luke, and John.',
		days: 14,
		filter: (book) => ['matthew', 'mark', 'luke', 'john'].includes(book.id),
	},
	{
		id: 'psalms-proverbs-31',
		name: 'Psalms & Proverbs in a Month',
		description: 'Wisdom and worship — Psalms and Proverbs across 31 days.',
		days: 31,
		filter: (book) => ['psalms', 'proverbs'].includes(book.id),
	},
	{
		id: 'ot-90',
		name: 'Old Testament in 90 Days',
		description: 'All 39 Old Testament books across three months.',
		days: 90,
		filter: (book) => book.testament === 'OT',
	},
];

function expandBooks(books) {
	const list = [];
	books
		.slice()
		.sort((a, b) => a.order - b.order)
		.forEach((book) => {
			for (let c = 1; c <= book.chapters; c++) {
				list.push({ book: book.id, bookName: book.name, chapter: c });
			}
		});
	return list;
}

/**
 * Expand a plan's book filter into one or more canonically-ordered chapter
 * lists. Non-"mixed" plans return a single list. "mixed" plans (currently
 * just Bible in a Year) return separate Old and New Testament lists so that
 * getDayReading() can pull from both every day, instead of finishing the
 * entire Old Testament before ever reaching the New.
 */
export function buildPlanChapterList(index, plan) {
	if (!index || !index.books) return [];

	const books = index.books.filter(plan.filter);

	if (!plan.mixed) {
		return [expandBooks(books)];
	}

	return [expandBooks(books.filter((b) => b.testament === 'OT')), expandBooks(books.filter((b) => b.testament === 'NT'))];
}

/**
 * Return the chapter entries assigned to a given 1-indexed day. Each list
 * (see buildPlanChapterList) is distributed evenly across the plan's day
 * count independently, then the day's slices are concatenated — so a
 * "mixed" plan's OT list and NT list each contribute their own reading on
 * every single day, rather than being read as one long concatenated block.
 */
export function getDayReading(chapterLists, totalDays, day) {
	const clampedDay = Math.max(1, Math.min(totalDays, day));

	return chapterLists.flatMap((list) => {
		if (list.length === 0) return [];
		const start = Math.floor(((clampedDay - 1) * list.length) / totalDays);
		const end = Math.floor((clampedDay * list.length) / totalDays);
		return list.slice(start, Math.max(end, start + 1));
	});
}

/**
 * How many calendar days have elapsed since startedAt (day 1 = start day),
 * clamped to the plan length.
 */
export function currentDayNumber(startedAt, totalDays) {
	const msPerDay = 24 * 60 * 60 * 1000;
	const elapsed = Math.floor((Date.now() - startedAt) / msPerDay) + 1;
	return Math.max(1, Math.min(totalDays, elapsed));
}
